<template>
  <v-container v-if="filter">
    <report
      :title="title"
      :filter="filter"
      :team="team"
      :manager="manager"
      :user="user"
      v-on:refreshData="refreshData"
      v-on:rowClick="rowClick"
      :headers="headers"
      :loading="list_loading"
      :items="items"
      :show-table="settings.isNonLitePlan"
    >
      <v-card-text class="py-0">
        <loading-spinner v-if="trend_loading"> </loading-spinner>
        <template v-else>
          <trend-chart
            :loading="trend_loading"
            :height="250"
            tooltip-append="%"
            round
            reverse
          ></trend-chart>
          <v-row v-if="participation.overview">
            <report-overall-card
              label="Participation"
              :value="$options.filters.no_decimal(completion) + '%'"
              :color="
                $coreScores.getColor($coreScores.reverseFormat(completion))
              "
            >
              <template v-slot:valueExtension>
                <compare-text
                  ignore-margin
                  align-end
                  v-if="participation.overview.userRecords.compare"
                  :old-value="
                    participation.overview.userRecords.compare.completion
                  "
                  :new-value="completion"
                  :timeframeId="filter.timeframeId"
                >
                </compare-text>
              </template>
              <v-progress-linear
                class="rounded-sm my-auto"
                :value="completion"
                background-color="background"
                :color="
                  $coreScores.getColor($coreScores.reverseFormat(completion))
                "
                height="8"
              ></v-progress-linear>
            </report-overall-card>
          </v-row>
        </template>
        <v-tabs v-model="tab" v-if="!filter.userId && settings.isNonLitePlan">
          <v-tab :class="{ 'd-none': filter.managerId }">
            Departments
          </v-tab>
          <v-tab
            :class="{
              'd-none':
                filter.managerId ||
                (participation.overview &&
                  participation.overview.hideManagers) ||
                !settings.showManagers
            }"
          >
            Teams
          </v-tab>
          <v-tab>
            Members
          </v-tab>
        </v-tabs>
      </v-card-text>
    </report>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import TrendChart from "@/components/reporting/TrendChart";
import CompareText from "@/components/reporting/CompareText";
import ReportOverallCard from "@/components/reporting/ReportOverallCard";
import reports from "@/helpers/mixins/reports";
import Report from "@/components/reporting/Report";
export default {
  name: "ParticipationReport",
  props: {
    title: { type: String },
    filter: { type: Object },
    team: { type: Object },
    manager: { type: Object },
    user: { type: Object }
  },
  components: {
    TrendChart,
    CompareText,
    ReportOverallCard,
    Report
  },
  mixins: [reports],
  data: () => ({
    list_loading: false,
    trend_loading: false,
    tab: 0
  }),
  watch: {
    tab(val, oldVal) {
      if (val != oldVal) {
        this.getList();
      }
    },
    filter(val, oldVal) {
      if (!oldVal && val != null) {
        this.update();
      }
    }
  },
  computed: {
    isSurvey() {
      return this.filter.featureId == this.$enums.features.SURVEYS;
    },
    headers() {
      if (this.filter.userId) {
        if (this.filter && this.filter.featureId) {
          let list = [{ text: "Sent On", value: "createdDate" }];
          switch (this.filter.featureId) {
            case this.$enums.features.REVIEW:
              list.push({
                text: "Questions",
                value: "questions",
                sortable: false
              });
              break;
            case this.$enums.features.RESOURCE:
              list.push({
                text: "Type",
                value: "resource.typeName"
              });
              list.push({
                text: "Resource",
                value: "resource.title"
              });
              break;
            case this.$enums.features.SURVEYS:
              list.push({
                text: "Survey",
                value: "survey.title"
              });
              break;
          }
          list.push({ text: "Status", value: "finished", sortable: false });

          if (this.filter.featureId == this.$enums.features.RESOURCE) {
            list.push({
              text: "",
              value: "resource.link",
              sortable: false
            });
          } else if (this.filter.featureId == this.$enums.features.SURVEYS) {
            list.push({
              text: "",
              value: "survey.link",
              sortable: false
            });
          }
          return list;
        }
        let list = [
          { text: "Type", value: "featureName" },
          { text: "Total Sent", value: "userRecords.value.sent" },
          { text: "Total Completed", value: "userRecords.value.finished" },
          { text: "Participation", value: "userRecords.value.completion" }
        ];
        if (this.filter.timeframeId != this.$enums.timeframes.ALL_TIME) {
          list.push({
            text: "Trend",
            value: "userRecords.percentChange"
          });
        }
        list.push({ text: "", value: "actions", sortable: false });
        return list;
      }

      let list = [
        { text: "Name", value: "name" },
        { text: "Participation", value: "userRecords.value.completion" }
      ];

      if (this.filter.timeframeId != this.$enums.timeframes.ALL_TIME) {
        list.push({
          text: "Trend",
          value: "userRecords.percentChange"
        });
      }
      list.push({ text: "", value: "actions", sortable: false });

      return list;
    },
    completion() {
      if (
        this.participation.overview &&
        this.participation.overview.userRecords &&
        this.participation.overview.userRecords.value
      ) {
        return this.participation.overview.userRecords.value.completion;
      }
      return 0;
    },
    items() {
      let data = [];
      switch (this.tab) {
        case 0:
          data = this.participation.teams;
          break;
        case 1:
          data = this.participation.managers;
          break;
        case 2:
          data = this.participation.members;
          break;
      }
      if (this.filter.userId) {
        data = this.participation.member;
      }
      return data || [];
    },
    ...mapGetters({
      participation: "reporting/participation",
      profile: "profile/profile",
      settings: "profile/settings"
    })
  },
  created() {
    if (this.filter != null) {
      this.update();
    }
  },
  methods: {
    rowClick(item) {
      let link = this.getLink(item);
      if (link == this.$route.path) {
        link += "/members";
      }
      if (!link) {
        return;
      }
      this.$router.push({ path: link });
    },
    update() {
      this.refreshData();
    },
    getList() {
      this.list_loading = true;
      Promise.all([this.$store.dispatch(this.getAction(), this.filter)])
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading report."
          );
        })
        .finally(() => {
          this.list_loading = false;
        });
    },
    getAction() {
      if (this.filter.userId) {
        return "reporting/getParticipationMember";
      }
      switch (this.tab) {
        case 0:
          return "reporting/getParticipationTeams";
        case 1:
          return "reporting/getParticipationManagers";
        default:
          return "reporting/getParticipationMembers";
      }
    },
    refreshData() {
      this.$store.dispatch("reporting/clearParticipation").then(() => {
        this.list_loading = true;
        this.trend_loading = true;
        Promise.all([
          this.$store.dispatch("reporting/getParticipation", this.filter),
          this.$store.dispatch("reporting/getParticipationTrend", this.filter)
        ])
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while loading report."
            );
          })
          .finally(() => {
            this.list_loading = false;
            this.trend_loading = false;
          })
          .then(() => {
            let newTab = this.getInitialTab();
            if (newTab) {
              this.tab = newTab;
            }
            this.getList();
          });
      });
    }
  }
};
</script>
